/**
 * Gemeinsamer Filterzustand des Prüfberichts.
 *
 * Der Filter ist bewusst blattbasiert: er reduziert die Blattliste
 * ({@link ReportLeaf}), aus der **beide** Ansichten aufgebaut werden. Damit wirkt dieselbe
 * Filterauswahl in der Spaltenansicht (die Spalten zeigen nur noch passende Items) und in der
 * Kartenansicht (leere Gruppen/Abschnitte fallen weg), ohne dass eine Ansicht eigene Filterlogik
 * mitbringt.
 */

import {NormalizedReport} from './normalized-report.model';
import {ReportDisplayConfig} from './report-display-config';
import {levelLabel} from './report-levels';
import {leafValue, ReportLeaf} from './report-leaves';

export type ReportStatus = 'success' | 'warning' | 'error' | 'unknown';

/** Aktive Filterauswahl; ein leeres Array bedeutet „keine Einschränkung". */
export interface ReportFilterState {
  files: string[];
  structures: string[];
  rules: string[];
  statuses: ReportStatus[];
}

export const EMPTY_REPORT_FILTER: ReportFilterState = {
  files: [], structures: [], rules: [], statuses: [],
};

/** Filterschlüssel → Ebenen-Key der Blattliste. */
const FILTER_LEVEL_KEYS: Record<Exclude<keyof ReportFilterState, 'statuses'>, string> = {
  files: 'files',
  structures: 'structure',
  rules: 'rules',
};

/** Anzeigetexte der Status-Filteroptionen. */
export const STATUS_FILTER_LABELS: Record<ReportStatus, string> = {
  success: 'Erfüllt',
  warning: 'Teilweise erfüllt',
  error: 'Nicht erfüllt',
  unknown: 'Unbekannt',
};

/**
 * Ein Filterblock für `app-bic-filter`. Strukturell kompatibel mit `BicFilterConfig`, aber ohne
 * Import aus dem Komponentenbaum – die Modelle bleiben frei von UI-Abhängigkeiten.
 */
export interface ReportFilterSection {
  key: string;
  displayName: string;
  type: string;
  options: string[];
  formatOption?: (value: any) => string;
}

/** Ob überhaupt eine Einschränkung aktiv ist. */
export function isFilterActive(filter: ReportFilterState | null | undefined): boolean {
  if (!filter) return false;
  return filter.files.length > 0 || filter.structures.length > 0
    || filter.rules.length > 0 || filter.statuses.length > 0;
}

/**
 * Reduziert die Blattliste auf die Auswahl. Der Statusfilter greift auf den Prüfregel-Status des
 * Blattes – das ist der Status, den beide Ansichten als Zeilen- bzw. Kartenstatus zeigen.
 */
export function filterReportLeaves(
  leaves: ReadonlyArray<ReportLeaf>,
  filter: ReportFilterState | null | undefined,
): ReportLeaf[] {
  if (!isFilterActive(filter)) return [...leaves];
  const active = filter!;
  return leaves.filter(leaf => {
    for (const [filterKey, levelKey] of Object.entries(FILTER_LEVEL_KEYS)) {
      const selected = active[filterKey as keyof typeof FILTER_LEVEL_KEYS];
      if (selected.length > 0 && !selected.includes(leafValue(leaf, levelKey))) return false;
    }
    return active.statuses.length === 0 || active.statuses.includes(leaf.rule.status);
  });
}

/**
 * Formatiert eine Prüfregel-Filteroption als Kürzel statt vollem (oft langen) Regelnamen.
 * Fällt auf den Namen zurück, wenn die Regel kein Kürzel trägt.
 */
function ruleShortCodeFormatter(leaves: ReadonlyArray<ReportLeaf>): (value: any) => string {
  const shortCodeByName = new Map<string, string>();
  for (const leaf of leaves) {
    const name = leaf.rule?.name;
    if (name && !shortCodeByName.has(name) && leaf.rule?.shortCode) {
      shortCodeByName.set(name, leaf.rule.shortCode);
    }
  }
  return (value: any) => shortCodeByName.get(String(value)) ?? String(value);
}

/** Distinct Werte einer Ebene über die Blätter, in Auftrittsreihenfolge. */
function distinctValues(leaves: ReadonlyArray<ReportLeaf>, levelKey: string): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const leaf of leaves) {
    const value = leafValue(leaf, levelKey);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    values.push(value);
  }
  return values;
}

/**
 * Baut die Filterblöcke aus dem Bericht: je dargestellter Gruppierungsebene ein Block mit den
 * tatsächlich vorkommenden Werten, plus ein Statusblock. Ebenen ohne Werte (z. B. keine Positionen)
 * entfallen, damit der Filter keine leeren Abschnitte zeigt.
 *
 * Die Optionen werden aus den **ungefilterten** Blättern gebildet – sonst könnte eine Auswahl die
 * Optionen entfernen, mit denen man sie zurücknehmen wollte.
 */
export function buildReportFilterSections(
  report: NormalizedReport | null | undefined,
  config: ReportDisplayConfig | null | undefined,
  leaves: ReadonlyArray<ReportLeaf>,
  levelKeys: ReadonlyArray<string>,
): ReportFilterSection[] {
  const sections: ReportFilterSection[] = [];

  for (const [filterKey, levelKey] of Object.entries(FILTER_LEVEL_KEYS)) {
    if (!levelKeys.includes(levelKey)) continue;
    const options = distinctValues(leaves, levelKey);
    if (options.length === 0) continue;
    sections.push({
      key: filterKey,
      displayName: levelLabel(config, levelKey),
      type: 'text',
      options,
      // Prüfregeln zeigen im Filtermenü das Kürzel statt des (oft langen) Regelnamens; gefiltert
      // wird weiterhin über den Namen, da `leafValue`/`filterReportLeaves` darauf matchen.
      formatOption: filterKey === 'rules' ? ruleShortCodeFormatter(leaves) : undefined,
    });
  }

  const statuses = (['success', 'warning', 'error', 'unknown'] as ReportStatus[])
    .filter(status => leaves.some(leaf => leaf.rule.status === status));
  if (statuses.length > 0) {
    sections.push({
      key: 'statuses',
      displayName: 'Status',
      type: 'status',
      options: statuses,
      formatOption: (value: any) => STATUS_FILTER_LABELS[value as ReportStatus] ?? String(value),
    });
  }

  return sections;
}

/** Übernimmt die rohe Auswahl von `app-bic-filter` in den typisierten Zustand (mit Kopien). */
export function fromBicFilters(raw: { [key: string]: any[] } | null | undefined): ReportFilterState {
  const asStrings = (value: any[] | undefined): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
  return {
    files: asStrings(raw?.['files']),
    structures: asStrings(raw?.['structures']),
    rules: asStrings(raw?.['rules']),
    statuses: asStrings(raw?.['statuses'])
      .filter((entry): entry is ReportStatus =>
        entry === 'success' || entry === 'warning' || entry === 'error' || entry === 'unknown'),
  };
}
